/**
 * MODULE: domain/pages/frontend/utils/slashCommands.ts
 * PURPOSE: Slash command definitions for the page editor
 */

import type { Editor } from '@tiptap/react';
import { isPagesAiEnabled } from './pageAiUtils';

export type SlashCommandId =
  | 'paragraph'
  | 'heading_1'
  | 'heading_2'
  | 'heading_3'
  | 'bulleted_list'
  | 'numbered_list'
  | 'quote'
  | 'divider'
  | 'code'
  | 'image'
  | 'file'
  | 'page_link'
  | 'bookmark'
  | 'action_button'
  | 'database_embed'
  | 'pipeline_snapshot'
  | 'crm_deal_preview'
  | 'crm_contact_preview'
  | 'crm_company_preview'
  | 'toggle'
  | 'callout'
  | 'table_of_contents'
  | 'tasks_sync'
  | 'transcript_preview'
  | 'todo'
  | 'ai_summarise'
  | 'ai_draft_email'
  | 'ai_call_prep'
  | 'ai_write';

export type SlashCommand = {
  id: SlashCommandId;
  label: string;
  description: string;
  keywords: string[];
  group: 'basic' | 'media' | 'links' | 'ai';
};

const AI_COMMANDS: SlashCommand[] = [
  {
    id: 'ai_summarise',
    label: 'AI summarise',
    description: 'Summarise this page with the assistant',
    keywords: ['ai', 'summarise', 'summary', 'tldr'],
    group: 'ai',
  },
  {
    id: 'ai_draft_email',
    label: 'AI draft email',
    description: 'Draft a sales follow-up email from these notes',
    keywords: ['ai', 'email', 'draft', 'follow up'],
    group: 'ai',
  },
  {
    id: 'ai_call_prep',
    label: 'AI call prep',
    description: 'Generate call prep talking points',
    keywords: ['ai', 'call', 'prep', 'meeting'],
    group: 'ai',
  },
  {
    id: 'ai_write',
    label: 'AI write',
    description: 'Custom prompt — ask the assistant anything',
    keywords: ['ai', 'write', 'prompt', 'generate'],
    group: 'ai',
  },
];

export const SLASH_COMMANDS: SlashCommand[] = [
  { id: 'paragraph', label: 'Text', description: 'Plain paragraph', keywords: ['text', 'paragraph', 'p'], group: 'basic' },
  { id: 'heading_1', label: 'Heading 1', description: 'Large section heading', keywords: ['h1', 'heading', 'title'], group: 'basic' },
  { id: 'heading_2', label: 'Heading 2', description: 'Medium section heading', keywords: ['h2', 'heading', 'subtitle'], group: 'basic' },
  { id: 'heading_3', label: 'Heading 3', description: 'Small section heading', keywords: ['h3', 'heading'], group: 'basic' },
  { id: 'bulleted_list', label: 'Bulleted list', description: 'Unordered list', keywords: ['bullet', 'ul', 'list'], group: 'basic' },
  { id: 'numbered_list', label: 'Numbered list', description: 'Ordered list', keywords: ['numbered', 'ol', 'list'], group: 'basic' },
  { id: 'quote', label: 'Quote', description: 'Capture a quote', keywords: ['quote', 'blockquote'], group: 'basic' },
  {
    id: 'toggle',
    label: 'Toggle list',
    description: 'Collapsible section with hidden content',
    keywords: ['toggle', 'collapse', 'accordion', 'dropdown'],
    group: 'basic',
  },
  {
    id: 'callout',
    label: 'Callout',
    description: 'Highlighted info, warning, or note box',
    keywords: ['callout', 'alert', 'info', 'warning', 'note'],
    group: 'basic',
  },
  {
    id: 'table_of_contents',
    label: 'Table of contents',
    description: 'Jump links to headings on this page',
    keywords: ['toc', 'contents', 'outline', 'index', 'table of contents'],
    group: 'basic',
  },
  {
    id: 'tasks_sync',
    label: 'Tasks sync',
    description: 'Live CRM task list linked to this page',
    keywords: ['tasks', 'sync', 'crm', 'checklist', 'action items'],
    group: 'links',
  },
  {
    id: 'transcript_preview',
    label: 'Call transcript',
    description: 'Live call transcript and AI summary preview',
    keywords: ['transcript', 'call', 'recording', 'summary', 'meeting'],
    group: 'links',
  },
  {
    id: 'todo',
    label: 'To-do',
    description: 'Checkbox task item',
    keywords: ['todo', 'task', 'checkbox', 'checklist'],
    group: 'basic',
  },
  { id: 'divider', label: 'Divider', description: 'Visual separator', keywords: ['divider', 'hr', 'line'], group: 'basic' },
  { id: 'code', label: 'Code', description: 'Code block', keywords: ['code', 'snippet'], group: 'basic' },
  { id: 'image', label: 'Image', description: 'Upload an image', keywords: ['image', 'photo', 'picture'], group: 'media' },
  { id: 'file', label: 'File', description: 'Upload a file attachment', keywords: ['file', 'attachment', 'pdf'], group: 'media' },
  {
    id: 'page_link',
    label: 'Link to page',
    description: 'Link another workspace page',
    keywords: ['page', 'link', 'mention', 'wiki'],
    group: 'links',
  },
  {
    id: 'bookmark',
    label: 'Bookmark',
    description: 'Save an external link with preview card or embed',
    keywords: ['bookmark', 'embed', 'url', 'link', 'website', 'video'],
    group: 'links',
  },
  {
    id: 'action_button',
    label: 'Action button',
    description: 'Call-to-action button linking to a URL or page',
    keywords: ['button', 'action', 'cta', 'link', 'call to action'],
    group: 'links',
  },
  {
    id: 'database_embed',
    label: 'Embed database',
    description: 'Inline live database table from this workspace',
    keywords: ['database', 'embed', 'table', 'inline', 'db', 'rows'],
    group: 'links',
  },
  {
    id: 'pipeline_snapshot',
    label: 'Pipeline snapshot',
    description: 'Live CRM pipeline KPIs and stage funnel',
    keywords: ['pipeline', 'snapshot', 'crm', 'funnel', 'deals', 'sales', 'kpi'],
    group: 'links',
  },
  {
    id: 'crm_deal_preview',
    label: 'Deal preview',
    description: 'Live CRM deal snapshot card',
    keywords: ['deal', 'crm', 'preview', 'sales', 'opportunity'],
    group: 'links',
  },
  {
    id: 'crm_contact_preview',
    label: 'Contact preview',
    description: 'Live CRM contact snapshot card',
    keywords: ['contact', 'person', 'crm', 'preview', 'lead'],
    group: 'links',
  },
  {
    id: 'crm_company_preview',
    label: 'Account preview',
    description: 'Live CRM account snapshot card',
    keywords: ['company', 'account', 'crm', 'preview', 'organization'],
    group: 'links',
  },
  ...(isPagesAiEnabled() ? AI_COMMANDS : []),
];

export function filterSlashCommands(query: string): SlashCommand[] {
  const q = query.trim().toLowerCase();
  if (!q) return SLASH_COMMANDS;
  return SLASH_COMMANDS.filter(
    (cmd) =>
      cmd.label.toLowerCase().includes(q) ||
      cmd.description.toLowerCase().includes(q) ||
      cmd.keywords.some((k) => k.includes(q) || q.includes(k))
  );
}

export function getSlashMatch(textBeforeCursor: string): { query: string; length: number } | null {
  const match = /^\/([^\s/]*)$/.exec(textBeforeCursor);
  if (!match) return null;
  return { query: match[1] ?? '', length: match[0].length };
}

export function runSlashCommand(editor: Editor, commandId: SlashCommandId): boolean {
  const chain = editor.chain().focus();
  switch (commandId) {
    case 'paragraph':
      return chain.setParagraph().run();
    case 'heading_1':
      return chain.toggleHeading({ level: 1 }).run();
    case 'heading_2':
      return chain.toggleHeading({ level: 2 }).run();
    case 'heading_3':
      return chain.toggleHeading({ level: 3 }).run();
    case 'bulleted_list':
      return chain.toggleBulletList().run();
    case 'numbered_list':
      return chain.toggleOrderedList().run();
    case 'quote':
      return chain.toggleBlockquote().run();
    case 'divider':
      return chain.setHorizontalRule().run();
    case 'code':
      return chain.toggleCodeBlock().run();
    case 'toggle':
      return chain
        .insertContent({
          type: 'pagesToggleBlock',
          attrs: { title: 'Toggle', body: '', open: true },
        })
        .run();
    case 'callout':
      return chain
        .insertContent({
          type: 'pagesCalloutBlock',
          attrs: { text: '', variant: 'info' },
        })
        .run();
    case 'table_of_contents':
      return chain
        .insertContent({
          type: 'pagesTocBlock',
          attrs: { title: 'On this page', maxDepth: 3 },
        })
        .run();
    case 'pipeline_snapshot':
      return chain
        .insertContent({
          type: 'pagesPipelineSnapshotBlock',
          attrs: {
            title: 'Pipeline snapshot',
            showKpis: true,
            showFunnel: true,
          },
        })
        .run();
    case 'todo':
      return chain
        .insertContent({
          type: 'pagesTodoBlock',
          attrs: { text: '', checked: false },
        })
        .run();
    case 'crm_deal_preview':
    case 'crm_contact_preview':
    case 'crm_company_preview':
    case 'tasks_sync':
    case 'transcript_preview':
    case 'bookmark':
    case 'action_button':
    case 'database_embed':
      return false;
    default:
      return false;
  }
}

export function slashCommandToAiPreset(
  commandId: SlashCommandId
): 'summarise' | 'draft_email' | 'call_prep' | 'custom' | null {
  switch (commandId) {
    case 'ai_summarise':
      return 'summarise';
    case 'ai_draft_email':
      return 'draft_email';
    case 'ai_call_prep':
      return 'call_prep';
    case 'ai_write':
      return 'custom';
    default:
      return null;
  }
}

export function isAiSlashCommand(commandId: SlashCommandId): boolean {
  return slashCommandToAiPreset(commandId) != null;
}
